import { getBrandTheme } from "../styles/brandTheme";
import bouttimeLogo from "../assets/bouttime-logo.svg";

const ChevronLeft = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const SECTIONS = [
  {
    title: "1. Introduction",
    body: "BoutTime (“we”, “us”, “our”) is committed to protecting your privacy. This Privacy Policy explains what personal data we collect, how we use it, and your rights in relation to it. This policy complies with the UK General Data Protection Regulation (UK GDPR).",
  },
  {
    title: "2. Data We Collect",
    items: [
      { label: "Account data", text: "Email address and password (encrypted) when you create an account." },
      { label: "Profile data", text: "Name, date of birth, biological sex, height, current weight, activity level, and nutrition goals — collected during onboarding to calculate your personalised nutrition targets." },
      { label: "Usage data", text: "Meal plans, completed meals, saved recipes, daily notes, and recipe notes you create within the App." },
      { label: "Technical data", text: "Basic analytics data including feature usage and app performance, collected via PostHog analytics. This data is anonymised where possible." },
    ],
  },
  {
    title: "3. How We Use Your Data",
    intro: "We use your data to:",
    bullets: [
      "Provide and personalise the BoutTime service",
      "Calculate your nutrition targets",
      "Save your meal plans and preferences",
      "Improve the App based on usage patterns",
      "Contact you about significant changes to the service",
    ],
    footer: "We do not sell your personal data to third parties. We do not use your data for advertising purposes.",
  },
  {
    title: "4. Data Storage",
    body: "Your data is stored securely using Supabase, a cloud database provider. Data is stored on servers within the European Economic Area. We implement appropriate technical and organisational measures to protect your data.",
  },
  {
    title: "5. Data Retention",
    body: "We retain your data for as long as your account is active. If you delete your account, your personal data will be permanently deleted within 30 days. Some anonymised analytics data may be retained for longer for service improvement purposes.",
  },
  {
    title: "6. Your Rights Under UK GDPR",
    intro: "You have the right to:",
    bullets: [
      "Access the personal data we hold about you",
      "Correct inaccurate data",
      "Delete your data (right to erasure)",
      "Restrict processing of your data",
      "Object to processing of your data",
      "Data portability — receive your data in a machine-readable format",
    ],
    footer: "To exercise any of these rights please contact us at hello@bouttime.app",
  },
  {
    title: "7. Cookies",
    body: "BoutTime uses minimal cookies necessary for the App to function, including authentication cookies to keep you logged in. We do not use advertising or tracking cookies.",
  },
  {
    title: "8. Third Party Services",
    intro: "BoutTime uses the following third party services:",
    bullets: [
      "Supabase — database and authentication (supabase.com/privacy)",
      "Vercel — hosting (vercel.com/legal/privacy-policy)",
      "PostHog — analytics (posthog.com/privacy)",
    ],
  },
  {
    title: "9. Children's Privacy",
    body: "BoutTime is not intended for users under 18 years of age. We do not knowingly collect data from children.",
  },
  {
    title: "10. Changes to This Policy",
    body: "We may update this Privacy Policy from time to time. We will notify you of significant changes via the App or by email. The date at the top of this page will always reflect the most recent update.",
  },
  {
    title: "11. Contact and Complaints",
    body: "For any privacy questions or to exercise your rights, contact us at hello@bouttime.app\n\nIf you are unhappy with how we handle your data you have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk.",
  },
  {
    title: "12. Data Controller",
    body: "BoutTime is the data controller for the personal data collected through the App.",
  },
];

const bodyStyle = (appTheme) => ({
  fontFamily: appTheme.fontBody,
  fontSize: "15px",
  color: appTheme.secondaryText,
  margin: 0,
  lineHeight: 1.7,
});

export default function PrivacyPage() {
  const appTheme = getBrandTheme();

  return (
    <div style={{
      minHeight: "100vh",
      background: appTheme.pageBackground,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    }}>
      {/* Sticky header */}
      <div style={{
        width: "100%",
        position: "sticky",
        top: 0,
        background: appTheme.pageBackground,
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px 24px",
        borderBottom: `1px solid rgba(27,67,50,0.08)`,
      }}>
        <button
          onClick={() => window.close()}
          style={{
            position: "absolute",
            left: "16px",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: appTheme.primaryText,
            padding: "8px",
            display: "flex",
            alignItems: "center",
            touchAction: "manipulation",
          }}
        >
          <ChevronLeft />
        </button>
        <img src={bouttimeLogo} alt="BoutTime" style={{ height: "28px", width: "auto" }} />
      </div>

      {/* Scrollable content */}
      <div style={{
        width: "100%",
        maxWidth: "680px",
        padding: "40px 24px 80px",
        boxSizing: "border-box",
      }}>
        <p style={{
          fontFamily: appTheme.fontBody,
          fontSize: "13px",
          color: appTheme.mutedText,
          marginBottom: "8px",
        }}>
          Last updated: May 2026
        </p>
        <h1 style={{
          fontFamily: appTheme.fontHeading,
          fontSize: "32px",
          fontWeight: "600",
          color: appTheme.primaryText,
          margin: "0 0 40px",
          lineHeight: 1.2,
        }}>
          Privacy Policy
        </h1>

        {SECTIONS.map(({ title, body, items, intro, bullets, footer }) => (
          <div key={title} style={{ marginBottom: "32px" }}>
            <h2 style={{
              fontFamily: appTheme.fontHeading,
              fontSize: "18px",
              fontWeight: "600",
              color: appTheme.primaryText,
              margin: "0 0 10px",
            }}>
              {title}
            </h2>

            {/* Plain paragraph */}
            {body && (
              <p style={{ ...bodyStyle(appTheme), whiteSpace: "pre-line" }}>{body}</p>
            )}

            {/* Labelled list (section 2) */}
            {items && (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {items.map(({ label, text }) => (
                  <p key={label} style={bodyStyle(appTheme)}>
                    <span style={{ fontWeight: "700", color: appTheme.primaryText }}>{label}:</span>{" "}{text}
                  </p>
                ))}
              </div>
            )}

            {/* Bullet list with optional intro and footer */}
            {bullets && (
              <>
                {intro && <p style={{ ...bodyStyle(appTheme), marginBottom: "10px" }}>{intro}</p>}
                <ul style={{ margin: "0 0 0 20px", padding: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
                  {bullets.map((item) => (
                    <li key={item} style={bodyStyle(appTheme)}>{item}</li>
                  ))}
                </ul>
                {footer && <p style={{ ...bodyStyle(appTheme), marginTop: "12px" }}>{footer}</p>}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
